import {
  Avatar,
  Card,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography
} from '@mui/material';
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getIcon } from 'src/utils/hex-to-rgba';
import Spinner from '../../components/spinner';
import { apiService } from '../../api/axiosClient';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import FindProductToolbar from './FindProductToolbar';

const currencyLocale = Intl.NumberFormat('ko-KR')

const columns = [
  {
    field: '__check__',
    hideable: false,
    sortable: false,
    filterable: false,
    headerAlign: 'left'
  },
  {
    minWidth: 200,
    field: 'sku',
    hideable: false,
    headerName: 'SKU',
    renderCell: params => (
      <Fragment>
        <Avatar alt="image" src={getIcon(params.row.icon1)} variant="rounded" />
        <Link to={`/admin/product/item/${params.row.sku}`} >
          <Typography variant='body2' sx={{ ml: 1, color: 'text.primary' }}>
            {params.row.sku}
          </Typography>
        </Link>
      </Fragment>
    )
  },
  {
    minWidth: 200,
    field: 'name',
    hideable: false,
    headerName: '이름',
    renderCell: params => (
      <Typography variant='body2' sx={{ color: 'text.primary' }}>
        {params.row.name}
      </Typography>
    )
  },
  {
    field: 'category',
    minWidth: 200,
    headerName: '카테고리',
    renderCell: params => (
      <Typography variant='body2' sx={{ color: 'text.primary' }}>
        {params.row.category}
      </Typography>
    )
  },
  {
    field: 'cat_id',
    headerName: 'Cat No',
    renderCell: params => (
      <Typography variant='body2' sx={{ color: 'text.primary' }}>
        {params.row.cat_id}
      </Typography>
    )
  },
  {
    field: 'pd_cas',
    headerName: 'CAS',
    renderCell: params => (
      <Typography variant='body2' sx={{ color: 'text.primary' }}>
        {params.row.pd_cas}
      </Typography>
    )
  },
  {
    field: 'spec_desc',
    minWidth: 200,
    headerName: 'Specification',
    renderCell: params => (
      <Typography variant='body2' sx={{ color: 'text.primary' }}>
        {params.row.spec_desc}
      </Typography>
    )
  },
  {
    field: 'pd_packing',
    headerName: 'Packing',
    renderCell: params => (
      <Typography variant='body2' sx={{ color: 'text.primary' }}>
        {params.row.pd_packing}
      </Typography>
    )
  },
  {
    field: 'pd_lead',
    headerName: '납기',
    renderCell: params => (
      <Typography variant='body2' sx={{ color: 'text.primary' }}>
        {params.row.pd_lead}
      </Typography>
    )
  },
  {
    field: 'brand',
    headerName: '브랜드',
    renderCell: params => (
      <Typography variant='body2' sx={{ color: 'text.primary' }}>
        {params.row.brand}
      </Typography>
    )
  },
  {
    field: 'price',
    headerName: '단가',
    renderCell: params => (
      <Typography variant='body2' sx={{ color: 'text.primary' }}>
        {currencyLocale.format(params.row.price)}원
      </Typography>
    )
  }
]

const FindProduct = (props) => {
  const {reload, setReload, selected, setSelected} = props;
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState('asc')
  const [pageSize, setPageSize] = useState(10)
  const [list, setList] = useState([]);
  const [searchTarget, setSearchTarget] = useState('name')
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState('sku')
  const [selectionModel, setSelectionModel] = useState([]);

  const loadColumnOrderTable = window.localStorage.getItem('columnFindProduct') ? 
  JSON.parse(window.localStorage.getItem('columnFindProduct')) : 
  {
    category: true,
    brand: true,
    price: true,
    insert_time: true,
  };
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(loadColumnOrderTable);

  const loadProducts = 
    async (query, target, sort, column) => {
      try {
        const response = await apiService.get('/products', {
          params: {
            query,
            target,
            sort,
            column,
            page,
            pageSize
          }
        });
        const payload = response.data;
        setTotal(payload.size);
        setList(payload.list);
        setLoading(false);
      } catch (error) {
        console.error(error);
      }
    }

  useEffect(() => {
    loadProducts(searchValue, searchTarget, sort, sortColumn)
  }, [page, pageSize, reload])

  useEffect(() => {
    window.localStorage.setItem( 'columnFindProduct', JSON.stringify(columnVisibilityModel) );
    setColumnVisibilityModel(columnVisibilityModel);
  }, [columnVisibilityModel])

  useEffect(() => {
    setSelected({ ...selected, [page]: selectionModel })
  }, [selectionModel])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadProducts(searchValue, searchTarget, newModel[0].sort, newModel[0].field)
    } else {
      setSort('asc')
      setSortColumn('sku')
    }
  }

  const handleSearch = value => {
    setSearchValue(value)
    loadProducts(value, searchTarget, sort, sortColumn)
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  return (
    loading ?
    <Spinner/>
    :
    <Card>
      <CardHeader title='등록상품' />
      <DataGrid
        sx={{
          ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.sku}
        pagination
        rows={list}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: FindProductToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            keyword: searchTarget,
            value: searchValue,
            clearSearch: () => handleSearch(''),
            onKeywordChange: event => setSearchTarget(event.target.value),
            onChange: event => handleSearch(event.target.value),
            numSelected: selected.length,
            selected,
            reload,
            setReload
          }
        }}
        checkboxSelection
        onSelectionModelChange={(newSelection) => {
          setSelectionModel(newSelection)
        }}
        selectionModel={selected[page]}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibilityModel(newModel)
        }
      />
    </Card>
  )
}

export default FindProduct
